import { LayoutContext } from '@lib/site-context'
import { screens } from '@lib/theme'
import {
  type CSSProperties,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

interface ProductHeroContentPositionProps {
  children: ReactNode
}

const recalculateEventName = 'product-hero-content:recalculate'

const ProductHeroContentPosition = ({
  children,
}: ProductHeroContentPositionProps) => {
  const { headerHeight, breadcrumbsHeight } = useContext(LayoutContext)

  const ref = useRef<HTMLDivElement | null>(null)
  const scrollYRef = useRef(0)
  const isSmallScreenRef = useRef(false)
  const windowDimensionsRef = useRef(0)
  const elementHeightRef = useRef(0)
  const elementTopRef = useRef(0)
  const windowHeightRef = useRef(0)
  const scrollYTreasholdRef = useRef(0)
  const prevStyleRef = useRef<CSSProperties>({})
  const rafIdRef = useRef(0)

  const [style, setStyle] = useState<CSSProperties>({})

  useEffect(() => {
    // Set element top
    elementTopRef.current = headerHeight + breadcrumbsHeight

    const element = ref.current

    if (element) {
      // Trigger recalculate event
      element.dispatchEvent(
        new Event(recalculateEventName, {
          bubbles: true,
        }),
      )
    }
  }, [breadcrumbsHeight, headerHeight])

  // Using recalculate event and window resize and scroll events handle element styling
  useEffect(() => {
    function setScreenDependantVars() {
      const element = ref.current

      if (!element) {
        return
      }

      windowDimensionsRef.current = window.innerWidth + window.innerHeight
      isSmallScreenRef.current =
        window.innerWidth < Number(screens.sm.replace('px', ''))

      if (!isSmallScreenRef.current) {
        windowHeightRef.current = window.innerHeight
        elementHeightRef.current = element.clientHeight

        scrollYTreasholdRef.current = Math.max(
          elementHeightRef.current -
            windowHeightRef.current +
            elementTopRef.current,
          0,
        )
      }
    }

    function applyStyle(newStyle: CSSProperties) {
      const prev = prevStyleRef.current

      if (prev.position === newStyle.position && prev.top === newStyle.top) {
        return
      }

      prevStyleRef.current = newStyle
      setStyle(newStyle)
    }

    function calcPlacement() {
      const element = ref.current
      const parentElement = element?.parentElement

      if (!element || !parentElement) {
        return
      }

      if (isSmallScreenRef.current) {
        applyStyle({})
        return
      }

      // Batch DOM reads
      const elementBounds = element.getBoundingClientRect()
      const parentElementBounds = parentElement.getBoundingClientRect()
      const scrollY = window.scrollY
      const isScrolledPastPosition = scrollY >= scrollYRef.current
      scrollYRef.current = Math.max(scrollY, 0)

      // 1. If element height fits within viewport, stick it to the top
      if (
        elementTopRef.current + elementHeightRef.current <=
        windowHeightRef.current
      ) {
        applyStyle({
          position: 'sticky',
          top: elementTopRef.current,
        })
        return
      }

      // 2. If scrolled past position and reached the threshold, stick it to the bottom of the viewport
      if (
        isScrolledPastPosition &&
        Math.abs(elementBounds.top - elementTopRef.current) >=
          scrollYTreasholdRef.current
      ) {
        applyStyle({
          position: 'sticky',
          top: windowHeightRef.current - elementHeightRef.current,
        })
        return
      }

      // 3. If scrolling up and element is below the nav, stick it to the top
      if (
        !isScrolledPastPosition &&
        elementBounds.top >= elementTopRef.current
      ) {
        applyStyle({
          position: 'sticky',
          top: elementTopRef.current,
        })
        return
      }

      // 4. By default use relative positioning
      applyStyle({
        position: 'relative',
        top: elementBounds.top - parentElementBounds.y,
      })
    }

    // Initialize values on load
    setScreenDependantVars()
    calcPlacement()

    function handleScroll() {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = requestAnimationFrame(calcPlacement)
    }
    document.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: false,
    })

    function handleResize() {
      if (
        windowDimensionsRef.current !==
        window.innerWidth + window.innerHeight
      ) {
        setScreenDependantVars()
        calcPlacement()
      }
    }
    window.addEventListener('resize', handleResize, {
      passive: true,
      capture: false,
    })

    function handleStickyRecalculate() {
      setScreenDependantVars()
      calcPlacement()
    }
    document.addEventListener(
      recalculateEventName,
      handleStickyRecalculate,
      true,
    )

    return () => {
      cancelAnimationFrame(rafIdRef.current)
      document.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
      document.removeEventListener(
        recalculateEventName,
        handleStickyRecalculate,
      )
    }
  }, [])

  return (
    <div ref={ref} style={style}>
      {children}
    </div>
  )
}

export default ProductHeroContentPosition
